import DateTimeComponents from './DateTimeComponents';
import NumberUtility from './NumberUtility';

type DateTimeParsableType = string|Date|DateTimeComponents

type DateComponentsType = {
  year?: string,
  month?: string,
  day?: string,
}

type TimeComponentsType = {
  hour: string,
  minute: string,
  second: string,
}

export default class DateTimeUtility {
  static isDateLikeObject(value: any): value is DateComponentsType {
    return value instanceof Object && 'year' in value && 'month' in value && 'day' in value;
  }

  static isTimeLikeObject(value: any): value is TimeComponentsType {
    return value instanceof Object && 'hour' in value && 'minute' in value && 'second' in value;
  }

  static parseDate(value: DateTimeParsableType, defaultComponents: DateComponentsType): DateComponentsType {
    let year = '';
    let month = '';
    let day = '';
    if (defaultComponents) {
      if (typeof defaultComponents.year !== 'undefined') {
        year = defaultComponents.year;
      }
      if (typeof defaultComponents.month !== 'undefined') {
        month = defaultComponents.month;
      }
      if (typeof defaultComponents.day !== 'undefined') {
        day = defaultComponents.day;
      }
    }
    if (DateTimeUtility.isDateLikeObject(value)) {
      if (typeof value.year !== 'undefined') {
        year = value.year;
      }
      if (typeof value.month !== 'undefined') {
        month = value.month;
      }
      if (typeof value.day !== 'undefined') {
        day = value.day;
      }
    } else if (value !== null) {
      const newValue = String(value);
      const date = this.toDate(newValue);
      if (date !== null) {
        year = `${date.getFullYear()}`;
        month = `${date.getMonth() + 1}`;
        day = `${date.getDate()}`;
      }
    }
    return { year, month, day };
  }

  static parseTime(value: DateTimeParsableType, defaultComponents: TimeComponentsType) {
    let hour = '';
    let minute = '';
    let second = '';
    if (defaultComponents) {
      if ('hour' in defaultComponents) {
        hour = defaultComponents.hour;
      }
      if ('minute' in defaultComponents) {
        minute = defaultComponents.minute;
      }
      if ('second' in defaultComponents) {
        second = defaultComponents.second;
      }
    }
    if (DateTimeUtility.isTimeLikeObject(value)) {
      hour = value.hour;
      minute = value.minute;
      second = value.second;
    } else if (value !== null) {
      const newValue = String(value);
      const matches = newValue.match(/^([0-9]+):([0-9]+):([0-9]+)$/);
      if (matches) {
        hour = NumberUtility.zeroFill(parseInt(matches[1], 10), 2);
        minute = NumberUtility.zeroFill(parseInt(matches[2], 10), 2);
        second = NumberUtility.zeroFill(parseInt(matches[3], 10), 2);
      }
    }
    return { hour, minute, second };
  }

  /**
   * @param {string|Date|object} value
   * @returns Date|null
   */
  static toDate(value: DateTimeParsableType): Date|null {
    if (value instanceof Date) return value;
    if (value instanceof Object) return value.toDate()
    const components = DateTimeComponents.fromISO8601(value);
    if (components === null) return null;
    return components.toDate();
  }

  static toDateTimeComponents(value: DateTimeParsableType) {
    if (value instanceof Date) {
      return DateTimeComponents.fromDate(value);
    }
    if (value instanceof Object) {
      const now = DateTimeComponents.now();
      const year = value.year || now.year;
      const month = value.month || now.month;
      const day = value.day || now.day;
      const hour = value.hour || now.hour;
      const minute = value.minute || now.minute;
      const second = value.second || now.second;
      return new DateTimeComponents(year, month, day, hour, minute, second);
    }
    return DateTimeComponents.fromISO8601(value);
  }

  static format(date: DateTimeParsableType, format: string): string {
    const components = DateTimeUtility.toDateTimeComponents(date);
    if (components === null) return '';
    return components.format(format);
  }

  /**
   * 誕生日から現在の年齢を取得
   */
  static getAgeForBirth(date: DateTimeParsableType): number|null {
    const birth = DateTimeUtility.toDateTimeComponents(date);
    if (birth === null) return null;
    const now = DateTimeComponents.now();
    const birthInYear = new DateTimeComponents(now.year, birth.month, birth.day, 0, 0, 0);
    let age = now.year - birth.year;
    if (now.earlierThan(birthInYear)) {
      age -= 1;
    }
    return age;
  }
}
